// Injected once when the widget mounts. Scoped by .lcw-* so we don't leak into
// host CSS. Tokens mirror the admin console's design system (dark + violet).
import type { WidgetConfig } from './types.js';

const FONT_ID = 'lcw-outfit-font';

function loadFont() {
  if (document.getElementById(FONT_ID)) return;
  const link = document.createElement('link');
  link.id = FONT_ID;
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap';
  document.head.appendChild(link);
}

/**
 * `brandColor` overrides the primary if the site wants a different hue; blank
 * keeps the design-system violet. Placement comes from the admin settings and
 * is emitted as custom properties, so the same rules serve both corners.
 */
/**
 * The two palettes.
 *
 * Every colour in the widget resolves through these, including the faint
 * overlays used for quoted text and file cards -- those were hardcoded whites,
 * which simply vanish on a pale background. `veil` is that overlay: white on
 * dark, black on light.
 */
const DARK_TOKENS = `
      --lcw-bg:         oklch(0.135 0.006 292);
      --lcw-card:       oklch(0.178 0.007 292);
      --lcw-card-2:     oklch(0.235 0.008 292);
      --lcw-fg:         oklch(0.985 0 0);
      --lcw-muted-fg:   oklch(0.700 0.010 292);
      --lcw-border:     rgb(255 255 255 / 0.10);
      --lcw-input-bg:   oklch(0.178 0.007 292);
      --lcw-input-bd:   rgb(255 255 255 / 0.14);
      --lcw-primary-fg: oklch(0.160 0 0);
      --lcw-veil-5:     rgb(255 255 255 / 0.05);
      --lcw-veil-6:     rgb(255 255 255 / 0.06);
      --lcw-veil-7:     rgb(255 255 255 / 0.07);
      --lcw-veil-8:     rgb(255 255 255 / 0.08);
      --lcw-veil-10:    rgb(255 255 255 / 0.10);
      --lcw-veil-11:    rgb(255 255 255 / 0.11);
      --lcw-veil-14:    rgb(255 255 255 / 0.14);
      --lcw-shadow:     0 24px 60px -18px rgb(0 0 0 / 0.65);`;

const LIGHT_TOKENS = `
      --lcw-bg:         oklch(1 0 0);
      --lcw-card:       oklch(0.975 0.003 292);
      --lcw-card-2:     oklch(0.945 0.005 292);
      --lcw-fg:         oklch(0.210 0.010 292);
      --lcw-muted-fg:   oklch(0.505 0.012 292);
      --lcw-border:     rgb(0 0 0 / 0.10);
      --lcw-input-bg:   oklch(1 0 0);
      --lcw-input-bd:   rgb(0 0 0 / 0.14);
      /* White on the accent: the brand colour is chosen for a launcher on a
         customer's page, and dark text on it is only legible if they picked a
         pale one. */
      --lcw-primary-fg: oklch(1 0 0);
      --lcw-veil-5:     rgb(0 0 0 / 0.04);
      --lcw-veil-6:     rgb(0 0 0 / 0.05);
      --lcw-veil-7:     rgb(0 0 0 / 0.06);
      --lcw-veil-8:     rgb(0 0 0 / 0.07);
      --lcw-veil-10:    rgb(0 0 0 / 0.08);
      --lcw-veil-11:    rgb(0 0 0 / 0.09);
      --lcw-veil-14:    rgb(0 0 0 / 0.12);
      --lcw-shadow:     0 24px 60px -18px rgb(15 23 42 / 0.22);`;

export function injectStyles(
  config: Pick<WidgetConfig, 'brandColor' | 'position' | 'sideSpacing' | 'bottomSpacing' | 'theme'>,
) {
  loadFont();
  if (document.getElementById('lcw-styles')) return;
  const accent = config.brandColor;
  const primary = accent && accent !== '#2563eb' ? accent : 'oklch(0.685 0.190 293)';

  // 'auto' follows the visitor's own system setting, which is what a widget
  // dropped onto someone else's site should do by default -- a dark panel on a
  // light page reads as broken rather than deliberate.
  const scoped = (tokens: string) => `.lcw-launcher, .lcw-panel, .lcw-tip {${tokens}
    }`;
  const themeBlock =
    config.theme === 'light'
      ? scoped(LIGHT_TOKENS)
      : config.theme === 'dark'
        ? scoped(DARK_TOKENS)
        : `${scoped(DARK_TOKENS)}
    @media (prefers-color-scheme: light) {
      ${scoped(LIGHT_TOKENS)}
    }`;
  // One of the two horizontal offsets is `auto`, so a single rule set can sit
  // in either corner without duplicating every position declaration.
  const onLeft = config.position === 'bottom-left';
  const side = `${config.sideSpacing}px`;
  const bottom = `${config.bottomSpacing}px`;
  const style = document.createElement('style');
  style.id = 'lcw-styles';
  style.textContent = `
    ${themeBlock}
    .lcw-launcher, .lcw-panel, .lcw-tip {
      --lcw-side-l:     ${onLeft ? side : 'auto'};
      --lcw-side-r:     ${onLeft ? 'auto' : side};
      --lcw-bottom:     ${bottom};
      --lcw-primary:    ${primary};
      --lcw-ring:       ${primary};
      /* Absolute, where this was 0.9rem. A rem resolves against the host
         page's <html> and nothing the widget can reach, so on a theme built
         the common way, on 'html { font-size: 10px }', every corner in the
         widget came out at 9px instead of 14. */
      --lcw-radius:     14px;
      /* The widget's own type anchor. Without it the panel inherits the
         host page's body size -- 20px on one customer's site -- and every
         element that inherits rather than stating a size of its own grows
         along with it. One absolute value here, rather than a fix in each
         descendant. */
      font-size: 16px;
      /* Given a name so every rule below can restate the stack instead of
         leaning on inheritance to carry it down. A host page styling a bare
         'label' or 'button' sits between this element and the control that
         needs the font, and whatever that rule sets is what gets inherited
         instead. A custom property cannot be intercepted that way --
         nothing on the host page knows this name. */
      --lcw-font: 'Outfit', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
      font-family: var(--lcw-font);
      /* One size for every text field in the widget. It matches the message
         bubbles rather than towering over them, and the touch bump below
         keeps it at the 16px iOS Safari demands before it will stop zooming
         the whole host page in when a field takes focus. */
      --lcw-field-size: 14px;
      -webkit-font-smoothing: antialiased;
      /* The rest of the type a host page can hand down. The reset below
         repeats these for everything inside the widget; they are needed
         here as well because that reset must not touch the two properties
         this rule exists to pin, and so cannot cover the roots too. */
      font-style: normal;
      text-transform: none;
      letter-spacing: normal;
      word-spacing: normal;
      text-indent: 0;
      text-shadow: none;
      color: var(--lcw-fg);
      box-sizing: border-box;
    }
    @media (pointer: coarse) {
      .lcw-launcher, .lcw-panel, .lcw-tip { --lcw-field-size: 16px; }
    }

    /* Everything inside the widget, reset to what the widget expects rather
       than to whatever the host page happens to set. The widget mounts in
       that page's own DOM and not a shadow root, so a theme's bare
       'button {}' or 'input {}' rule reaches our controls, and any property
       we never declare ourselves falls through to it -- which is how a
       "Sign out" button came out reading SIGN OUT on one customer's site.
       Declaring them here, ahead of every rule below, closes the whole
       class of bug at once: the widget's own rules are at least as specific
       and come later, so anything it does mean to set (the tighter
       letter-spacing on headings, the italics on a deleted message) still
       wins. */
    .lcw-launcher *, .lcw-panel *, .lcw-tip *,
    .lcw-launcher *::before, .lcw-panel *::before, .lcw-tip *::before,
    .lcw-launcher *::after, .lcw-panel *::after, .lcw-tip *::after {
      box-sizing: border-box;
      font-family: var(--lcw-font);
      font-size: inherit;
      font-style: normal;
      text-transform: none;
      letter-spacing: normal;
      word-spacing: normal;
      text-indent: 0;
      text-shadow: none;
    }

    /* Launcher ------------------------------------------------------------ */
    .lcw-launcher {
      position: fixed; left: var(--lcw-side-l); right: var(--lcw-side-r); bottom: var(--lcw-bottom); z-index: 2147483000;
      width: 60px; height: 60px; border-radius: 9999px;
      background: var(--lcw-primary); color: var(--lcw-primary-fg);
      display: flex; align-items: center; justify-content: center;
      /* The mascot rule below hides this shadow when a mascot is present -- the
         sprite has its own bubble outline and this would render as a disc
         behind it. It stays for an uploaded logo, which is clipped to the
         round bezel and does need the drop for depth. */
      box-shadow: 0 8px 24px -6px rgb(0 0 0 / 0.5), 0 2px 6px rgb(0 0 0 / 0.4);
      cursor: pointer; border: none;
      /* Buttons carry a UA padding of about 1px 6px. Harmless while the
         contents were a fixed-size glyph; once the mark fills the button it
         squashed a square logo to 44x54 and shoved it off-centre. */
      padding: 0; line-height: 0;
      transition: transform 220ms cubic-bezier(.22,1,.36,1);
    }
    /*
     * No lift on hover.
     *
     * Moving the button itself by a pixel when the cursor arrives means a
     * cursor resting on its edge loses it, which fires mouseleave, which drops
     * it back under the cursor — a flicker that made the hover message come and
     * go. The mark inside already animates on hover, so the lift added nothing
     * but that bug.
     */
    .lcw-launcher:active { transform: scale(0.96); }

    /*
     * Hit area a little larger than the button.
     *
     * The mark animates up to 9px above the button when it bobs, so the croc is
     * drawn outside the region that actually responds to a cursor. Pointing at
     * the raised croc did nothing. This extends the target without moving
     * anything visible.
     */
    .lcw-launcher::before {
      content: ''; position: absolute; inset: -10px;
      border-radius: inherit;
    }

    /*
     * A wave to catch the eye, then silence.
     *
     * Applied to the mark inside the button rather than the button itself, so
     * it does not fight the hover and press transforms above.
     *
     * Three plays and it stops. A launcher that jiggles forever stops being an
     * invitation and becomes something to ignore -- or to resent on a page
     * someone is trying to read. It also goes quiet permanently once the chat
     * has been opened: by then the visitor knows it is there.
     */
    @keyframes lcw-wave {
      0%, 55%, 100% { transform: translateY(0) rotate(0deg); }
      10%  { transform: translateY(-5px) rotate(-8deg); }
      20%  { transform: translateY(0)    rotate(6deg); }
      30%  { transform: translateY(-3px) rotate(-5deg); }
      40%  { transform: translateY(0)    rotate(3deg); }
    }
    .lcw-launcher > img, .lcw-launcher > svg {
      transform-origin: 50% 70%;
      animation: lcw-wave 2.8s ease-in-out 1.6s 3;
    }
    .lcw-launcher.lcw-quiet > img, .lcw-launcher.lcw-quiet > svg { animation: none; }
    /* After quiet, so hovering still wakes it once it has settled. */
    .lcw-launcher:hover > img, .lcw-launcher:hover > svg {
      animation: lcw-wave 0.9s ease-in-out 0s 1;
    }

    /*
     * A reply is waiting.
     *
     * This one does loop, unlike the greeting wave: there is something
     * unread, and it should keep saying so until the visitor looks. It stops
     * the moment the panel opens. Declared last so it outranks both quiet and
     * hover — an unread message matters more than either.
     */
    @keyframes lcw-alert {
      0%, 45%, 100% { transform: translateY(0) scale(1); }
      12% { transform: translateY(-9px) scale(1.07); }
      24% { transform: translateY(0)    scale(0.98); }
      34% { transform: translateY(-4px) scale(1.03); }
    }
    .lcw-launcher.lcw-alert > img, .lcw-launcher.lcw-alert > svg {
      animation: lcw-alert 1.6s ease-in-out infinite;
    }

    /* Unread count. Ringed in the panel background so it stays legible on a
       dark logo, a light one, or a photograph. */
    .lcw-badge {
      position: absolute; top: -2px; right: -2px;
      min-width: 20px; height: 20px; padding: 0 5px;
      border-radius: 999px; background: #ef4444; color: #fff;
      font-size: 11px; font-weight: 800; line-height: 20px; text-align: center;
      box-shadow: 0 0 0 2px var(--lcw-bg);
      pointer-events: none;
    }

    @media (prefers-reduced-motion: reduce) {
      .lcw-launcher > img, .lcw-launcher > svg,
      .lcw-launcher:hover > img, .lcw-launcher:hover > svg,
      .lcw-launcher.lcw-alert > img, .lcw-launcher.lcw-alert > svg { animation: none; }
    }
    .lcw-launcher svg    { width: 24px; height: 24px; stroke: currentColor; fill: none; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }

    /* Panel --------------------------------------------------------------- */
    .lcw-panel {
      position: fixed; left: var(--lcw-side-l); right: var(--lcw-side-r); bottom: calc(var(--lcw-bottom) + 76px); z-index: 2147483000;
      width: 380px; max-width: calc(100vw - 32px);
      height: 580px; max-height: calc(100vh - 120px);
      background: var(--lcw-bg);
      border: 1px solid var(--lcw-border);
      border-radius: calc(var(--lcw-radius) + 8px);
      box-shadow: var(--lcw-shadow);
      display: flex; flex-direction: column; overflow: hidden;
      animation: lcw-in 260ms cubic-bezier(.22,1,.36,1);
    }
    @keyframes lcw-in { from { opacity: 0; transform: translateY(8px) scale(0.98); } to { opacity: 1; transform: none; } }
    @media (prefers-reduced-motion: reduce) { .lcw-panel { animation: none; } }

    /* Header -------------------------------------------------------------- */
    .lcw-header {
      display: flex; align-items: center; gap: 10px;
      padding: 14px 16px;
      background: var(--lcw-card);
      border-bottom: 1px solid var(--lcw-border);
    }
    .lcw-header-chip {
      /* position: relative -- gives the logo image below a containing block to
         size against via inset: 0, rather than a percentage width/height that
         a flex item apparently cannot always resolve reliably (see the
         comment on .lcw-launcher img.lcw-logo). */
      position: relative;
      width: 32px; height: 32px; border-radius: calc(var(--lcw-radius) - 4px);
      background: color-mix(in oklab, var(--lcw-primary) 15%, transparent);
      color: var(--lcw-primary);
      display: flex; align-items: center; justify-content: center; flex: none;
    }
    .lcw-header-chip svg { width: 16px; height: 16px; stroke: currentColor; fill: none; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
    /* min-width: 0 overrides the flex item default of min-width: auto, which
       floors a flex item at its content's width no matter how much it is told
       to shrink. Without it, a brand name reported as one unbroken word (a
       slug, a URL, a name with no spaces) does not wrap -- flexbox instead
       ships the width the row would need to show it whole, and the close
       button gets crushed narrow and pushed out past the panel's edge,
       clipped there by .lcw-panel's overflow: hidden. overflow-wrap lets that
       one pathological word break rather than dictate the row's width; normal
       names with spaces already wrapped fine and are untouched by either
       rule. */
    .lcw-header-title  { font-weight: 700; font-size: 15px; letter-spacing: -0.01em; flex: 1; min-width: 0; overflow-wrap: anywhere; }
    /*
     * !important on the button chrome here, against a real customer site
     * where this rendered as a solid filled pill in the host theme's own
     * accent colour -- no override anywhere in any stylesheet could be found
     * for it (every selector on the page was checked against this exact
     * element), yet the plain declaration below consistently lost. An inline
     * style forcing the same values, added after the fact, did take -- so
     * this is not a lost specificity fight so much as this rule's first
     * application somehow not sticking. Forcing it is the same fix, done in
     * the stylesheet instead of after the fact.
     */
    .lcw-close {
      position: relative;
      background: transparent !important; color: var(--lcw-muted-fg); border: none !important;
      width: 32px !important; height: 32px !important; padding: 0 !important; border-radius: 9999px !important;
      display: flex; align-items: center; justify-content: center; flex: none;
      cursor: pointer;
    }
    .lcw-close:hover { background: var(--lcw-card-2); color: var(--lcw-fg); }
    /* position + inset, not a flex child sized by width/height alone -- the
       same fix as .lcw-launcher img.lcw-logo, and the same real-site symptom:
       this icon rendered at zero width as an ordinary flex child. */
    .lcw-close svg {
      position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
      width: 16px !important; height: 16px !important;
      stroke: currentColor; fill: none; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round;
    }

    /* Body / messages ---------------------------------------------------- */
    .lcw-body {
      flex: 1; overflow-y: auto; padding: 16px;
      background: var(--lcw-bg);
      display: flex; flex-direction: column; gap: 8px;
    }

    /* Scrollbars: hidden chrome, still scrollable by wheel/touch/keyboard.
       The panel is narrow enough that a platform scrollbar — full-width with
       stepper arrows on Windows — eats visible width and clashes with the
       dark surface. Applied to both scroll containers so they match. */
    .lcw-body, .lcw-form                       { scrollbar-width: none; -ms-overflow-style: none; }
    .lcw-body::-webkit-scrollbar,
    .lcw-form::-webkit-scrollbar               { width: 0; height: 0; }

    /* Typing indicator — three pulsing dots, WhatsApp-style. Collapsed to zero
       height when off so it never reserves space in the thread. */
    .lcw-typing {
      display: none; align-items: center; gap: 4px;
      padding: 0 16px 8px; background: var(--lcw-bg);
    }
    .lcw-typing.on { display: flex; }
    .lcw-typing span {
      width: 6px; height: 6px; border-radius: 50%;
      background: var(--lcw-muted-fg);
      animation: lcw-blink 1.4s infinite both;
    }
    .lcw-typing span:nth-child(2) { animation-delay: 0.2s; }
    .lcw-typing span:nth-child(3) { animation-delay: 0.4s; }
    @keyframes lcw-blink {
      0%, 80%, 100% { opacity: 0.25; transform: translateY(0); }
      40%           { opacity: 1;    transform: translateY(-2px); }
    }

    .lcw-msg {
      max-width: 80%; padding: 9px 13px; font-size: 14px; line-height: 1.4;
      word-wrap: break-word;
      border-radius: calc(var(--lcw-radius) + 4px);
      /* .lcw-body is a flex column, so without an explicit align-self every
         bubble stretches to the full 80% — a two-word reply rendered as wide
         as the panel. align-self also does the left/right placement, so the
         old margin-left:auto isn't needed. */
      align-self: flex-start;
    }
    .lcw-msg.visitor {
      background: var(--lcw-primary); color: var(--lcw-primary-fg);
      align-self: flex-end; border-bottom-right-radius: calc(var(--lcw-radius) - 4px);
    }
    .lcw-msg.admin {
      background: var(--lcw-card); color: var(--lcw-fg);
      border: 1px solid var(--lcw-border);
      border-bottom-left-radius: calc(var(--lcw-radius) - 4px);
    }
    .lcw-msg.deleted { font-style: italic; opacity: 0.6; }

    /* Quoted message. Tinted panel + left rule so it reads as "not the message
       itself" even inside a solid-colour bubble. */
    .lcw-quote {
      display: block; margin: 0 0 5px; padding: 4px 7px;
      border-left: 2px solid currentColor;
      border-radius: 5px; background: rgb(0 0 0 / 0.18);
      font-size: 11.5px; line-height: 1.35; opacity: 0.85;
    }
    .lcw-quote strong { display: block; font-weight: 600; }
    .lcw-quote span {
      display: block; overflow: hidden;
      text-overflow: ellipsis; white-space: nowrap;
    }
    .lcw-msg.admin .lcw-quote { background: var(--lcw-veil-6); }

    /* Reaction chips, sitting under the bubble content. */
    .lcw-reactions { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 5px; }
    .lcw-reactions button {
      display: inline-flex; align-items: center; gap: 3px;
      padding: 1px 6px; font-size: 11px; line-height: 1.5;
      border-radius: 999px; cursor: pointer; font-family: inherit;
      background: rgb(0 0 0 / 0.22); color: inherit;
      border: 1px solid transparent;
    }
    .lcw-msg.admin .lcw-reactions button { background: var(--lcw-veil-7); }
    .lcw-reactions button.mine { border-color: #7cc4ff; background: rgb(124 196 255 / 0.22); }

    /* Reply composer bar, between the thread and the input. */
    .lcw-reply-bar {
      display: none; align-items: flex-start; gap: 8px;
      padding: 8px 12px 0; background: var(--lcw-bg);
    }
    .lcw-reply-bar.on { display: flex; }
    .lcw-reply-preview {
      flex: 1; min-width: 0; padding: 4px 8px;
      border-left: 2px solid var(--lcw-primary);
      border-radius: 5px; background: var(--lcw-veil-5);
      font-size: 11.5px; line-height: 1.35;
    }
    .lcw-reply-preview strong { display: block; color: var(--lcw-primary); font-weight: 600; }
    .lcw-reply-preview span {
      display: block; color: var(--lcw-muted-fg);
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }
    .lcw-reply-cancel {
      background: none; border: 0; cursor: pointer; padding: 2px 4px;
      color: var(--lcw-muted-fg); font-size: 18px; line-height: 1;
    }

    .lcw-emoji-row {
      display: flex; gap: 2px; padding: 6px 8px;
      border-bottom: 1px solid var(--lcw-border);
    }
    .lcw-emoji-row button {
      padding: 2px 4px; font-size: 15px; line-height: 1;
      background: none; border: 0; cursor: pointer; border-radius: 5px;
    }
    .lcw-emoji-row button:hover { background: var(--lcw-veil-8); }

    /* Per-message action menu. The trigger only appears on hover/focus so the
       thread stays quiet; the bubble is the positioning context. */
    .lcw-msg { position: relative; }
    .lcw-msg-menu-btn {
      position: absolute; top: 2px; right: 4px;
      width: 16px; height: 16px; padding: 0; line-height: 1;
      display: flex; align-items: center; justify-content: center;
      background: none; border: 0; cursor: pointer;
      color: inherit; opacity: 0; transition: opacity 0.12s;
      font-size: 14px;
    }
    .lcw-msg:hover .lcw-msg-menu-btn,
    .lcw-msg-menu-btn:focus-visible { opacity: 0.7; }
    .lcw-msg.deleted .lcw-msg-menu-btn { display: none; }

    .lcw-msg-menu {
      position: absolute; top: 20px; right: 4px; z-index: 5;
      min-width: 150px; overflow: hidden;
      background: var(--lcw-card); color: var(--lcw-fg);
      border: 1px solid var(--lcw-border);
      border-radius: calc(var(--lcw-radius) - 2px);
      box-shadow: 0 8px 24px rgb(0 0 0 / 0.45);
      display: flex; flex-direction: column;
    }
    .lcw-msg-menu button {
      background: none; border: 0; cursor: pointer;
      text-align: left; padding: 8px 12px; font-size: 12px;
      color: inherit; font-family: inherit;
    }
    .lcw-msg-menu button:hover { background: var(--lcw-card-2); }
    .lcw-msg-menu button.danger { color: #ff8a8a; }
    /* Read receipts — grey single tick until the agent reads it, then a blue
       double tick, matching the convention people already know.
       Inline rather than block: it trails the last word the way WhatsApp does,
       instead of claiming a whole row and making every bubble taller. */
    .lcw-ticks {
      display: inline-block; vertical-align: baseline;
      margin-left: 6px; line-height: 1;
      /* -2px at 10px overlapped the two check glyphs enough that the second
         one looked sheared off. Slightly larger with a gentler tuck keeps them
         legible as a pair. */
      font-size: 11px; letter-spacing: -0.5px;
      color: var(--lcw-primary-fg); opacity: 0.55;
    }
    .lcw-ticks.read { color: #7cc4ff; opacity: 1; }
    /* Media bubbles stack, so the tick would land alone under the image —
       pull it back onto the bubble's bottom-right corner instead. */
    .lcw-msg:has(img) .lcw-ticks,
    .lcw-msg:has(audio) .lcw-ticks { display: block; text-align: right; margin: 4px 0 0; }

    /* Height cap as well as width: the panel is only ~500px tall, so a portrait
       photo constrained by width alone fills the whole thread. */
    .lcw-msg img    {
      max-width: 100%; max-height: 200px; width: auto; object-fit: contain;
      border-radius: calc(var(--lcw-radius) - 2px); display: block;
    }
    /* Document card. A bare filename link says nothing about what the file is;
       the icon and size do that at a glance. */
    .lcw-doc {
      display: flex; align-items: center; gap: 9px;
      width: 220px; max-width: 100%; padding: 7px;
      border-radius: calc(var(--lcw-radius) - 2px);
      background: rgb(0 0 0 / 0.18);
      text-decoration: none; color: inherit;
    }
    .lcw-msg.admin .lcw-doc { background: var(--lcw-veil-6); }
    .lcw-doc:hover { background: rgb(0 0 0 / 0.28); }
    .lcw-msg.admin .lcw-doc:hover { background: var(--lcw-veil-11); }

    .lcw-doc-tile {
      flex: 0 0 auto; width: 34px; height: 34px;
      display: grid; place-items: center;
      border-radius: 8px; color: oklch(0.99 0 0);
    }
    .lcw-doc-tile svg { width: 17px; height: 17px; }

    .lcw-doc-text { flex: 1; min-width: 0; display: block; }
    .lcw-doc-text strong {
      display: block; font-size: 12.5px; font-weight: 600; line-height: 1.25;
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }
    .lcw-doc-text span { display: block; font-size: 10.5px; opacity: 0.7; margin-top: 1px; }
    .lcw-doc-dl { flex: 0 0 auto; opacity: 0.6; }
    .lcw-doc-dl svg { width: 14px; height: 14px; }

    /* :not(.lcw-doc) — the document card is an <a>, and this rule is declared
       later, so it was re-underlining the filename inside the card. */
    .lcw-msg a:not(.lcw-doc):not(.lcw-card) { color: inherit; text-decoration: underline; text-underline-offset: 3px; }

    /* Link card: a link the website has described (a picture, what it is, a
       button). One <a>, so the whole card is the link. */
    .lcw-card {
      display: block; width: 250px; max-width: 100%; margin-top: 7px;
      border-radius: calc(var(--lcw-radius) - 2px); overflow: hidden;
      background: rgb(0 0 0 / 0.18);
      text-decoration: none; color: inherit;
      transition: background 0.12s;
    }
    .lcw-msg > .lcw-card:first-child { margin-top: 2px; }
    /* A bubble that holds a card is as wide as the card, not as its text. */
    .lcw-msg.has-card { padding: 7px; }
    .lcw-msg.has-card > span:not(.lcw-ticks) { display: block; padding: 2px 6px 0; }
    .lcw-msg.has-card > .lcw-ticks { display: block; text-align: right; margin: 4px 4px 0; }
    .lcw-msg.admin .lcw-card { background: var(--lcw-veil-6); }
    .lcw-card:hover { background: rgb(0 0 0 / 0.28); }
    .lcw-msg.admin .lcw-card:hover { background: var(--lcw-veil-11); }
    .lcw-card:focus-visible { outline: 2px solid var(--lcw-ring); outline-offset: 2px; }
    /* Undoes the photo rules above: this picture fills the card and is not
       something to zoom into. */
    .lcw-msg .lcw-card-img {
      display: block; width: 100%; max-width: none; max-height: none;
      aspect-ratio: 16 / 9; object-fit: cover; border-radius: 0; cursor: pointer;
    }
    .lcw-card-body { display: block; padding: 10px 11px 11px; }
    .lcw-card-label {
      display: block; margin-bottom: 3px;
      font-size: 10.5px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase;
      opacity: 0.7;
    }
    .lcw-card-title { display: block; font-size: 13.5px; font-weight: 600; line-height: 1.3; }
    .lcw-card-desc { display: block; margin-top: 3px; font-size: 12px; line-height: 1.4; opacity: 0.82; }
    .lcw-card-cta {
      display: block; margin-top: 10px; padding: 7px 10px;
      border-radius: 9px; text-align: center;
      font-size: 12.5px; font-weight: 600;
      background: var(--lcw-primary); color: var(--lcw-primary-fg);
    }
    .lcw-card-host { display: block; margin-top: 6px; text-align: center; font-size: 10.5px; opacity: 0.6; }
    /* On the visitor's own bubble the bubble is the brand colour already. */
    .lcw-msg.visitor .lcw-card-cta { background: var(--lcw-primary-fg); color: var(--lcw-primary); }
    @media (prefers-reduced-motion: reduce) { .lcw-card { transition: none; } }

    /* Image preview. Covers the viewport rather than the panel: on a phone the
       panel is only a few hundred px wide, which is no better than the inline
       thumbnail. */
    .lcw-msg img { cursor: zoom-in; }
    .lcw-lightbox {
      position: fixed; inset: 0; z-index: 2147483000;
      display: flex; align-items: center; justify-content: center;
      padding: 24px; background: rgb(0 0 0 / 0.82);
      backdrop-filter: blur(2px);
    }
    .lcw-lightbox img {
      max-width: 100%; max-height: 100%;
      border-radius: 10px; object-fit: contain;
      box-shadow: 0 20px 60px rgb(0 0 0 / 0.5);
    }
    .lcw-lightbox-close {
      position: absolute; top: 14px; right: 14px;
      width: 34px; height: 34px; padding: 0; cursor: pointer;
      display: grid; place-items: center;
      border: 0; border-radius: 50%;
      background: var(--lcw-veil-14); color: var(--lcw-fg);
    }
    .lcw-lightbox-close svg { width: 17px; height: 17px; }
    /* Voice notes. The native <audio controls> chrome is light-themed, wide,
       and carries a volume slider and overflow menu the panel has no room for,
       so the player is rebuilt from parts. currentColor throughout means the
       same markup works on both the purple and the card bubble. */
    .lcw-msg audio { display: none; }
    /* Fixed width for the same reason as images: the bubble is shrink-to-fit,
       so a percentage width would collapse the player. */
    .lcw-msg video {
      display: block; width: 240px; max-width: 100%; max-height: 220px;
      border-radius: calc(var(--lcw-radius) - 2px); background: #000;
    }
    .lcw-voice {
      display: flex; align-items: center; gap: 9px;
      width: 200px; max-width: 100%;
    }
    .lcw-voice-play {
      flex: 0 0 auto; width: 30px; height: 30px; padding: 0;
      display: grid; place-items: center;
      border: 0; border-radius: 50%; cursor: pointer;
      color: inherit; background: rgb(0 0 0 / 0.22);
    }
    .lcw-msg.admin .lcw-voice-play { background: var(--lcw-veil-10); }
    .lcw-voice-play svg { width: 14px; height: 14px; }

    .lcw-voice-bars {
      flex: 1; min-width: 0; height: 24px; cursor: pointer;
      display: flex; align-items: center; gap: 2px;
    }
    .lcw-voice-bars span {
      flex: 1; min-width: 2px; border-radius: 999px;
      background: currentColor; opacity: 0.35;
    }
    .lcw-voice-bars span.played { opacity: 0.95; }

    .lcw-voice-time {
      flex: 0 0 auto; font-size: 11px; opacity: 0.75;
      font-variant-numeric: tabular-nums;
    }

    /* Onboarding form ----------------------------------------------------- */
    .lcw-form {
      padding: 20px; display: flex; flex-direction: column; gap: 12px;
      flex: 1; overflow-y: auto;
    }
    .lcw-hint { font-size: 13px; color: var(--lcw-muted-fg); margin: 0; }
    .lcw-form label {
      display: flex; flex-direction: column; gap: 6px;
      font-size: 13px; font-weight: 500; color: var(--lcw-fg);
    }
    .lcw-form input {
      height: 40px; width: 100%;
      background: var(--lcw-input-bg); color: var(--lcw-fg);
      border: 1px solid var(--lcw-input-bd);
      border-radius: calc(var(--lcw-radius) + 4px);
      padding: 0 14px; outline: none;
      /* Spelled out rather than 'font: inherit'. A form control inherits no
         type of its own, and what these were inheriting was the 13px of the
         <label> wrapping them -- so the address someone is proof-reading for
         a typo was set smaller than the hint above it. */
      font-family: var(--lcw-font); font-size: var(--lcw-field-size); font-weight: 400;
      transition: border-color 150ms, box-shadow 150ms;
    }
    .lcw-form input:focus-visible {
      border-color: var(--lcw-ring);
      box-shadow: 0 0 0 3px color-mix(in oklab, var(--lcw-ring) 25%, transparent);
    }
    .lcw-form input::placeholder { color: var(--lcw-muted-fg); }

    .lcw-otp-area { display: none; flex-direction: column; gap: 12px; margin-top: 4px; }
    .lcw-otp-area.on { display: flex; }
    .lcw-error {
      display: none; font-size: 12px; color: #fca5a5;
      background: rgb(239 68 68 / 0.10); border: 1px solid rgb(239 68 68 / 0.25);
      padding: 8px 10px; border-radius: calc(var(--lcw-radius) - 2px);
    }
    .lcw-error.on { display: block; }

    /* Buttons ------------------------------------------------------------- */
    .lcw-btn {
      display: inline-flex; align-items: center; justify-content: center; gap: 6px;
      background: var(--lcw-primary); color: var(--lcw-primary-fg);
      border: none; border-radius: 9999px;
      padding: 0 20px; height: 40px;
      font: 600 14px/1 var(--lcw-font); letter-spacing: -0.01em;
      cursor: pointer;
      transition: filter 150ms, transform 100ms;
    }
    .lcw-btn:hover:not(:disabled) { filter: brightness(1.05); }
    .lcw-btn:active:not(:disabled) { transform: scale(0.98); }
    .lcw-btn:disabled { opacity: 0.5; cursor: not-allowed; }
    .lcw-btn.ghost {
      background: transparent; color: var(--lcw-fg);
      border: 1px solid var(--lcw-border);
    }
    .lcw-btn.ghost:hover { background: var(--lcw-card-2); }
    /* flex: none because every .icon button sits in a flex row next to
       .lcw-input, which is told to shrink (see its min-width: 0 below). Without
       this the icon buttons shrink too -- flex-shrink defaults to 1 regardless
       of an explicit width -- so on a narrow panel the attach, mic and send
       buttons got squeezed toward zero width and, being last in the row, send
       was pushed past the panel's edge and clipped by its overflow: hidden. A
       narrowed input is still usable; a narrowed send button is not, so the
       input is where the row's missing space should come from. */
    .lcw-btn.icon { width: 40px; padding: 0; flex: none; }
    .lcw-btn svg  { width: 16px; height: 16px; stroke: currentColor; fill: none; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }

    /* Composer ------------------------------------------------------------ */
    .lcw-blocked {
      margin: 8px 12px 12px;
      padding: 10px 12px;
      border-radius: 12px;
      background: var(--lcw-muted, rgba(127,127,127,.12));
      color: var(--lcw-fg, inherit);
      font-size: 13px;
      text-align: center;
    }
    .lcw-blocked[hidden] { display: none; }
    .lcw-notice {
      margin: 6px 12px 0;
      padding: 8px 12px;
      border-radius: 12px;
      background: oklch(0.83 0.12 75 / 0.25);
      color: var(--lcw-fg, inherit);
      font-size: 13px;
    }
    .lcw-notice[hidden] { display: none; }
    .lcw-composer {
      border-top: 1px solid var(--lcw-border);
      padding: 10px;
      display: flex; align-items: center; gap: 8px;
      background: var(--lcw-card);
    }
    .lcw-composer .lcw-input {
      /* min-width: 0 overrides a text input's own default min-content floor
         (a browser-defined width, roughly 20 characters, unrelated to what is
         actually typed) so it can shrink all the way down when the row is
         tight -- see the .lcw-btn.icon comment for why it has to be this
         element doing the shrinking. */
      flex: 1; min-width: 0; height: 40px;
      background: var(--lcw-input-bg); color: var(--lcw-fg);
      border: 1px solid var(--lcw-input-bd);
      border-radius: 9999px;
      padding: 0 16px; outline: none;
      /* 'font: inherit' here meant the message someone types was set at the
         host page's body size -- 20px on one customer's site, against 14px
         bubbles directly above it, which is what made the field look blown
         up. Stated outright, it is the widget's own size everywhere. */
      font-family: var(--lcw-font); font-size: var(--lcw-field-size); font-weight: 400;
    }
    .lcw-composer .lcw-input:focus-visible {
      border-color: var(--lcw-ring);
      box-shadow: 0 0 0 3px color-mix(in oklab, var(--lcw-ring) 25%, transparent);
    }
    .lcw-composer .lcw-input::placeholder { color: var(--lcw-muted-fg); }

    .lcw-btn.mic.recording {
      background: rgb(239 68 68 / 0.15); color: #fca5a5;
      animation: lcw-pulse 1.4s ease-in-out infinite;
    }
    @keyframes lcw-pulse { 0%, 100% { box-shadow: 0 0 0 0 rgb(239 68 68 / 0.30); } 50% { box-shadow: 0 0 0 6px rgb(239 68 68 / 0); } }
    @media (prefers-reduced-motion: reduce) { .lcw-btn.mic.recording { animation: none; } }

    /* Hover tooltip beside the closed launcher. pointer-events:none so it can
       never sit between the cursor and the button it is describing. */
    /* The greeting sits open from the moment the widget loads. It used to
       appear only on hover, which meant a phone never saw it at all and a
       visitor had to find the launcher before being told why to press it.
       Being permanent, it needs a way out -- hence the close button, and
       pointer-events back on so it can be pressed. */
    .lcw-tip {
      /* Stacked above the launcher, not beside it: sitting beside it meant its
         width reached most of the way across a phone screen, landing on top
         of whatever a host page already has fixed in the *opposite* corner --
         a WhatsApp or call button, most often. Stacking keeps both of the
         widget's own elements inside one corner column at any viewport width. */
      position: fixed; bottom: calc(var(--lcw-bottom) + 76px); z-index: 2147482999;
      ${onLeft ? 'left: var(--lcw-side-l);' : 'right: var(--lcw-side-r);'}
      /* max-content, or the flex row shrinks to its narrowest word and wraps
         a six-word greeting onto three lines. Capped against the viewport too,
         so a long message on a phone cannot run under the launcher. */
      width: max-content; max-width: max(150px, min(232px, calc(100vw - 108px)));
      padding: 9px 8px 9px 12px;
      display: flex; align-items: flex-start; gap: 6px;
      background: var(--lcw-card-2); color: var(--lcw-fg);
      border: 1px solid var(--lcw-border); border-radius: 10px;
      font-size: 13px; line-height: 1.35; font-weight: 500;
      box-shadow: 0 8px 24px -8px rgb(0 0 0 / 0.55);
      opacity: 0; transform: translateY(4px);
      pointer-events: none;
    }
    /* Offset only. An animation that touches opacity governs it for the whole
       active phase, and a phase that never advances -- background tab, page not
       being painted -- leaves the greeting at 0 forever. Stuck at the from-state
       this costs four pixels of position and nothing else. */
    @keyframes lcw-tip-in { from { transform: translateY(4px); } to { transform: translateY(0); } }
    /* Visible by its own declarations; the animation only decorates the
       entrance. A "both" fill was wrong here -- it holds the from-state whenever the
       animation does not actually run, which is the case in a background tab,
       under reduced-motion, and anywhere frames are not being painted. The
       greeting would simply never appear, with nothing to show why. */
    .lcw-tip.on {
      opacity: 1; transform: translateY(0); pointer-events: auto;
      animation: lcw-tip-in 180ms ease;
    }
    .lcw-tip-text { cursor: pointer; flex: 1 1 auto; }
    /* Same defensive !important and position + inset as .lcw-close, above --
       the same small icon-button shape, so it is a candidate for the same
       real-site collision even where it has not been observed directly. */
    .lcw-tip-close {
      position: relative;
      flex: none; width: 20px !important; height: 20px !important; margin-top: -1px; padding: 0 !important;
      display: flex; align-items: center; justify-content: center;
      background: none !important; border: none !important; border-radius: 6px !important; cursor: pointer;
      color: var(--lcw-muted-fg);
    }
    .lcw-tip-close:hover { background: var(--lcw-card); color: var(--lcw-fg); }
    .lcw-tip-close svg {
      position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
      width: 13px !important; height: 13px !important;
      stroke: currentColor; stroke-width: 2.2; fill: none; stroke-linecap: round;
    }
    @media (prefers-reduced-motion: reduce) { .lcw-tip.on { animation: none; } }

    /* Brand logo.
       On the launcher it fills the whole circle rather than sitting as a small
       badge on the accent colour — an uploaded mark is the thing people should
       recognise, not a swatch with something in the middle of it. object-fit
       covers so a non-square upload is cropped rather than stretched into
       something the customer never approved. */
    .lcw-launcher:has(img.lcw-logo) { background: transparent; }
    /* The stand-in when the mark fails to load; matches the built-in glyph. */
    .lcw-mark-fallback { display: flex; align-items: center; justify-content: center; }
    .lcw-mark-fallback svg { width: 24px; height: 24px; stroke: currentColor; fill: none; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
    .lcw-launcher img.lcw-logo {
      /* Absolutely positioned and filled via inset, not a percentage
         width/height on a flex item (which is what this used to be).
         Reproduced live on a real customer site: with width/height: 100% as a
         flex child of .lcw-launcher, the browser sometimes resolves this
         image to zero width -- reliably, on that page, on a cold load, no
         host CSS override involved -- leaving the launcher's mark invisible.
         Taking it out of flex sizing entirely with position + inset sidesteps
         whatever that resolution bug is rather than chasing its cause further.
         max-width/max-height: none stays too, since a host page's own
         "img { max-width: 100% }" reset (LiteSpeed Cache and others like it)
         is a real thing this could still collide with otherwise. */
      position: absolute; inset: 0; width: 100%; height: 100%;
      max-width: none; max-height: none;
      border-radius: 9999px; object-fit: cover; display: block;
    }
    /*
     * The mascot is a shaped sprite, not a photograph in a circle.
     *
     * 21% of its pixels are transparent -- a green speech bubble with a tail
     * sitting on a see-through canvas. Round-cropping it (as an uploaded logo
     * is cropped) cut off the tail and forced a bubble to sit inside a circle
     * it never fit in. So it renders whole, at its own outline, with a subtle
     * drop-shadow via the filter property that follows the shape, not a rectangle.
     *
     * The launcher's own round background and box-shadow are hidden here for
     * the same reason -- either would render as a disc behind the bubble and
     * read as broken trim.
     */
    .lcw-launcher img.lcw-mascot {
      object-fit: contain; border-radius: 0;
      filter: drop-shadow(0 3px 6px rgb(0 0 0 / 0.28));
    }
    .lcw-launcher:has(img.lcw-mascot) {
      background: transparent;
      box-shadow: none;
    }
    .lcw-header-chip:has(img.lcw-logo) { background: transparent; }
    .lcw-header-chip img.lcw-logo {
      /* Same flex-item sizing issue as the launcher's own logo, above. */
      position: absolute; inset: 0; width: 100%; height: 100%;
      max-width: none; max-height: none;
      border-radius: 9999px; object-fit: cover; display: block;
    }

    /* Same defensive !important as .lcw-close, above -- reported oversized
       on the same real customer site, both the button chrome and the text. */
    .lcw-signout {
      background: none !important; border: none !important; cursor: pointer; padding: 4px 6px !important;
      color: var(--lcw-muted-fg); font: inherit; font-size: 12px !important; font-weight: 600;
      border-radius: 8px !important; flex: none;
    }
    .lcw-signout:hover { color: var(--lcw-fg); background: var(--lcw-card-2); }

    /* Reaction row shown on hover, when configured that way. Anchored to the
       bubble rather than the menu so it appears without a click. */
    .lcw-hover-emoji {
      position: absolute; top: -14px; display: flex; gap: 2px; padding: 3px 5px;
      background: var(--lcw-card-2); border: 1px solid var(--lcw-border);
      border-radius: 999px; box-shadow: 0 6px 18px -8px rgb(0 0 0 / 0.6);
      z-index: 3;
    }
    .lcw-hover-emoji button {
      background: none; border: none; cursor: pointer; font-size: 15px;
      line-height: 1; padding: 2px; border-radius: 6px;
    }
    .lcw-hover-emoji button:hover { background: var(--lcw-card); transform: scale(1.15); }

    /* The hosted chat page, where the widget is the page rather than a corner
       of someone else's. Centred and taller, and it drops the fixed corner
       offsets so it doesn't hug an edge on a wide screen. */
    .lcw-panel.lcw-standalone {
      /* Top-anchored rather than centred: dead-centre covered the page's own
         heading, which is the one thing telling someone whose chat this is.
         Stretching between two edges also means it fills a tall window and
         still fits a short one without a height calculation. */
      left: 50%; right: auto;
      top: 104px; bottom: 24px;
      transform: translateX(-50%);
      width: min(440px, calc(100vw - 32px));
      height: auto; max-height: none;
    }
    @media (max-height: 560px) {
      /* On a short window the heading is a luxury; give the chat the room. */
      .lcw-panel.lcw-standalone { top: 16px; bottom: 16px; }
    }
  `;
  document.head.appendChild(style);
}
